// Per-key send scheduler: many commits in one window produce one send (invariant 29).
//
// A short debounce batches a commit burst, but the flush happens no later than `maxWaitMs`
// after the FIRST unflushed commit. A resettable debounce alone lets continuous editing push
// the deadline forever and starve the send. A failed flush retries on a doubling backoff until
// it lands, so edits made while the gateway is down reach it without waiting for the next commit.

export interface SchedulerOptions {
  throttleMs?: number;
  maxWaitMs?: number;
  retryBaseMs?: number;
  retryMaxMs?: number;
  now?: () => number;
}

const DEFAULT_THROTTLE_MS = 250;
const DEFAULT_MAX_WAIT_MS = 2_000;
const DEFAULT_RETRY_BASE_MS = 2_000;
const DEFAULT_RETRY_MAX_MS = 30_000;

interface Entry {
  send: () => Promise<void>;
  timer: ReturnType<typeof setTimeout> | undefined;
  // Not reset by a later schedule(): this is the maxWait anchor.
  firstPendingAt: number;
  inFlight: Promise<void> | undefined;
  followUp: boolean;
  failures: number;
}

export class SendScheduler {
  private readonly throttleMs: number;
  private readonly maxWaitMs: number;
  private readonly retryBaseMs: number;
  private readonly retryMaxMs: number;
  private readonly now: () => number;
  private readonly entries = new Map<string, Entry>();
  private closed = false;

  constructor(options: SchedulerOptions = {}) {
    this.throttleMs = options.throttleMs ?? DEFAULT_THROTTLE_MS;
    this.maxWaitMs = options.maxWaitMs ?? DEFAULT_MAX_WAIT_MS;
    this.retryBaseMs = options.retryBaseMs ?? DEFAULT_RETRY_BASE_MS;
    this.retryMaxMs = options.retryMaxMs ?? DEFAULT_RETRY_MAX_MS;
    this.now = options.now ?? Date.now;
  }

  schedule(key: string, send: () => Promise<void>): void {
    if (this.closed) return;
    const existing = this.entries.get(key);
    if (existing !== undefined) {
      existing.send = send;
      if (existing.inFlight !== undefined) {
        existing.followUp = true;
        return;
      }
      if (existing.timer !== undefined) clearTimeout(existing.timer);
      const capRemaining = existing.firstPendingAt + this.maxWaitMs - this.now();
      const delay = Math.max(0, Math.min(this.throttleMs, capRemaining));
      existing.timer = setTimeout(() => { void this.run(key); }, delay);
      return;
    }
    const entry: Entry = {
      send,
      timer: undefined,
      firstPendingAt: this.now(),
      inFlight: undefined,
      followUp: false,
      failures: 0,
    };
    entry.timer = setTimeout(() => { void this.run(key); }, this.throttleMs);
    this.entries.set(key, entry);
  }

  async flush(key: string): Promise<void> {
    const entry = this.entries.get(key);
    if (entry === undefined) return;
    if (entry.timer !== undefined) {
      clearTimeout(entry.timer);
      entry.timer = undefined;
    }
    await this.run(key);
  }

  async flushAll(): Promise<void> {
    await Promise.all([...this.entries.keys()].map((key) => this.flush(key)));
  }

  cancel(key: string): void {
    const entry = this.entries.get(key);
    if (entry === undefined) return;
    if (entry.timer !== undefined) clearTimeout(entry.timer);
    this.entries.delete(key);
  }

  close(): void {
    this.closed = true;
    for (const key of [...this.entries.keys()]) this.cancel(key);
  }

  private async run(key: string): Promise<void> {
    const entry = this.entries.get(key);
    if (entry === undefined) return;
    if (entry.inFlight !== undefined) {
      await entry.inFlight;
      return;
    }
    entry.timer = undefined;
    const promise = (async () => {
      try {
        await entry.send();
        entry.failures = 0;
      } catch (e) {
        entry.failures += 1;
        console.error('SendScheduler: send failed', e);
      }
    })();
    entry.inFlight = promise;
    await promise;
    entry.inFlight = undefined;
    if (this.closed) {
      this.entries.delete(key);
      return;
    }
    if (entry.failures > 0) {
      // Failure wins over a queued follow-up. The follow-up's commit is already durable in the
      // outbox and the next attempt carries it, so letting followUp reschedule at throttleMs
      // would turn the 2s..30s backoff into a ~250ms hammer during an outage the user edits through.
      entry.followUp = false;
      const backoff = Math.min(this.retryBaseMs * 2 ** (entry.failures - 1), this.retryMaxMs);
      entry.timer = setTimeout(() => { void this.run(key); }, backoff);
    } else if (entry.followUp) {
      entry.followUp = false;
      entry.firstPendingAt = this.now();
      entry.timer = setTimeout(() => { void this.run(key); }, this.throttleMs);
    } else if (entry.timer === undefined) {
      this.entries.delete(key);
    }
  }
}
